import type { Booking, BookingGarment } from '../api/bookings';
import { formatDate, formatDateTime } from '../format/dates';
import { formatAmount } from '../format/money';
import { liveGarments } from '../print/customerMoney';
import { depositSplit } from './deposit';
import { issuableGarments } from './issue';
import { stageMoveTo, type StageMove } from './stageMoves';
import { isOutOnRent } from './tones';

/**
 * The one thing that happens next on a booking — the booking redesign's step 4, worded once for
 * the website and the phone.
 *
 * <p>
 * <strong>One answer, never a list.</strong> A booking offered Hold, Confirm, Hand over, Take a
 * payment, Discount and Change the garments as equals, and the clerk had to work out which came
 * next. The screens lead with this one, say what it involves under it, and keep the rest in a
 * quieter row. Null means nothing is waiting on the counter: a finished booking, a cancelled one,
 * or a reader who may do none of it.
 * </p>
 *
 * <p>
 * <strong>Only what the reader may do, and only what the server would take.</strong> The
 * hand-over counts what the issue screen lets a clerk tick (`issuableGarments`), so the button never
 * promises garments the next screen then refuses to offer.
 * </p>
 */
export type NextStep =
  | {
      kind: 'confirm';
      /** The stage move to send — a hold confirms as "Confirm hold". */
      move: StageMove;
      label: string;
      detail: string;
    }
  | { kind: 'handOver'; label: string; detail: string | null; money: StepMoney | null }
  | { kind: 'takeBack'; label: string; detail: string; late: boolean }
  | {
      kind: 'payment';
      paymentKind: 'RentalBalance' | 'Deposit';
      label: string;
      detail: string;
      money: StepMoney;
    }
  | { kind: 'settle'; label: string; detail: string; money: StepMoney }
  /** Nothing to press: the garments are being got ready. Said, so the screen is not silent. */
  | { kind: 'waiting'; label: string; detail: string | null };

/**
 * The money a step moves, for a screen that leads with the figure (the phone's booking bar, owner
 * 2026-10-19) rather than the sentence in `detail`, which still says the same thing in words.
 */
export interface StepMoney {
  /** "To collect at hand-over", "Still to pay". */
  label: string;
  /** The total, never including GST — the client never works tax out. */
  amount: number;
  /** What makes it up — "Balance ₹4,500 · Deposit ₹6,750 · plus any GST" — or null when one thing. */
  parts: string | null;
  /** Anything else the step involves, said beside the figure — a garment still at the tailor. */
  note: string | null;
}

export interface NextStepPowers {
  /** `RentalOrder:Edit`, on a plan that has not lapsed — stage moves, hand-over, take-back. */
  act: boolean;
  /** `RentalOrder:ViewFinancials` — taking money and settling the deposit. */
  money: boolean;
}

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

export function nextStep(
  booking: Booking,
  powers: NextStepPowers,
  { currency = '' }: { currency?: string } = {},
): NextStep | null {
  const cash = (amount: number) => `${currency}${formatAmount(amount)}`;
  const live = liveGarments(booking);
  const out = live.filter((garment) => isOutOnRent(garment.state));
  const ready = booking.wearers.flatMap(issuableGarments);
  const waiting = live.filter(isBeingReadied);
  const deposit = depositSplit(booking.deposit);
  const balance = Math.max(0, booking.balanceDue);
  const going = booking.stage === 'Confirmed' || booking.stage === 'InProgress';

  // Before anything is set aside, the step is the decision. Quote and hold stay in the quieter row:
  // confirming is what the counter is working towards.
  const confirm = powers.act ? stageMoveTo(booking, 'Confirmed') : null;

  if (confirm) {
    return {
      kind: 'confirm',
      move: confirm,
      label: booking.stage === 'Hold' ? 'Confirm the hold' : 'Confirm the booking',
      detail:
        booking.stage === 'Hold' && booking.holdExpiresOn
          ? `The hold lapses ${formatDateTime(booking.holdExpiresOn)} unless it is confirmed.`
          : 'Sets the garments aside for the dates and gives it a BK number.',
    };
  }

  // Late beats everything: a garment somebody else is waiting for is the call to make first.
  if (powers.act && out.length > 0 && booking.isOverdue) {
    return {
      kind: 'takeBack',
      label: `Take back ${plural(out.length, 'garment', 'garments')}`,
      detail: `${plural(booking.daysOverdue, 'day', 'days')} late — due back ${formatDate(booking.possessionToDate)}.`,
      late: true,
    };
  }

  if (powers.act && going && ready.length > 0) {
    const collect = [
      balance > 0 ? cash(balance) : null,
      deposit.due > 0 ? `the ${cash(deposit.due)} deposit` : null,
    ].filter(Boolean);

    const toCollect = balance + deposit.due;

    return {
      kind: 'handOver',
      label: `Hand over ${plural(ready.length, 'garment', 'garments')}`,
      detail: joined([
        // "Any", because a shop that is not registered charges none and the client never works it
        // out — the hand-over screen asks the server what its invoice adds.
        collect.length > 0 ? `Collect ${collect.join(' and ')}, plus any GST` : null,
        waitingWords(waiting),
      ]),
      money:
        toCollect > 0
          ? {
              label: 'To collect at hand-over',
              amount: toCollect,
              parts: joined([
                balance > 0 ? `Balance ${cash(balance)}` : null,
                deposit.due > 0 ? `Deposit ${cash(deposit.due)}` : null,
                'plus any GST',
              ]),
              note: waitingWords(waiting),
            }
          : null,
    };
  }

  if (powers.act && out.length > 0) {
    return {
      kind: 'takeBack',
      label: `Take back ${plural(out.length, 'garment', 'garments')}`,
      detail: `Due back ${formatDate(booking.possessionToDate)}.`,
      late: false,
    };
  }

  // Everything is back and the shop refunds later — after inspection or cleaning — so the deposit
  // is the one thing still open. A shop refunding at the counter never gets here: the return
  // settled it.
  if (powers.money && booking.deposit.state === 'Held' && booking.stage === 'Completed') {
    return {
      kind: 'settle',
      label: 'Settle the deposit',
      detail: `${cash(deposit.held)} held — refund it, less anything the return found.`,
      money: {
        label: 'Deposit held',
        amount: deposit.held,
        parts: 'Refund it, less anything the return found',
        note: null,
      },
    };
  }

  if (powers.money && balance > 0) {
    return {
      kind: 'payment',
      paymentKind: 'RentalBalance',
      label: `Take ${cash(balance)}`,
      detail: joined(['Still to pay on the booking', going ? waitingWords(waiting) : null]) ?? '',
      money: {
        label: 'Still to pay',
        amount: balance,
        parts: null,
        note: going ? waitingWords(waiting) : null,
      },
    };
  }

  // A shop that takes the deposit at booking is owed it before pickup; one taking it at pickup
  // collects it with the hand-over above, so it is not a step of its own.
  if (powers.money && going && deposit.due > 0 && booking.deposit.collectionPoint !== 'AtPickup') {
    return {
      kind: 'payment',
      paymentKind: 'Deposit',
      label: `Take the ${cash(deposit.due)} deposit`,
      detail: 'The shop takes it before pickup.',
      money: {
        label: 'Deposit to take',
        amount: deposit.due,
        parts: 'The shop takes it before pickup',
        note: null,
      },
    };
  }

  if (going && waiting.length > 0) {
    return {
      kind: 'waiting',
      label: `Getting ${plural(waiting.length, 'garment', 'garments')} ready`,
      detail: waitingWords(waiting),
    };
  }

  return null;
}

/**
 * On the booking but unable to go out yet — at the tailor, or not reserved. A garment merely set
 * aside is not in this list: it can be handed over as it is (`isReadyToIssue`).
 */
export function isBeingReadied(garment: BookingGarment): boolean {
  return garment.state === 'Pending' || garment.state === 'Alteration';
}

/** "Navy Tuxedo at the tailor until 18-12-2026", or "2 not ready yet". */
export function waitingWords(waiting: readonly BookingGarment[]): string | null {
  const [only] = waiting;

  if (waiting.length === 0 || !only) {
    return null;
  }

  if (waiting.length > 1) {
    return `${waiting.length} not ready yet`;
  }

  if (only.state === 'Alteration') {
    return `${only.itemName} at ${only.alterationTailorName ?? 'the tailor'}${
      only.alterationDueOn ? ` until ${formatDate(only.alterationDueOn)}` : ''
    }`;
  }

  return `${only.itemName} not ready yet`;
}

function joined(parts: readonly (string | null)[]): string | null {
  const said = parts.filter((part): part is string => Boolean(part));

  return said.length > 0 ? said.join(' · ') : null;
}
